<template>
  <div class="document-upload">
    <div class="upload-area"
         :class="{ 'drag-over': isDragOver }"
         @drop="handleDrop"
         @dragover="handleDragOver"
         @dragleave="handleDragLeave"
         @click="triggerFileInput">

      <input
          ref="fileInput"
          type="file"
          multiple
          :accept="acceptedFileTypes"
          @change="handleFileSelect"
          style="display: none;"
      >

      <div class="upload-content">
        <i class="fas fa-cloud-upload-alt upload-icon"></i>
        <h6>Click to upload or drag and drop</h6>
        <p class="text-muted small">
          Supported formats: {{ allowedTypes.join(', ').toUpperCase() }}
          <br>Maximum file size: {{ maxSize }}MB each
        </p>
      </div>
    </div>

    <!-- File List -->
    <div v-if="uploadedFiles.length > 0" class="uploaded-files mt-3">
      <h6>Uploaded Documents:</h6>
      <div class="file-list">
        <div v-for="(file, index) in uploadedFiles" :key="index" class="file-item">
          <div class="file-info">
            <i :class="getFileIcon(file.type)" class="file-icon"></i>
            <div class="file-details">
              <span class="file-name">{{ file.name }}</span>
              <span class="file-size">{{ formatFileSize(file.size) }}</span>
            </div>
          </div>
          <div class="file-actions">
            <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                @click="removeFile(index)"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Progress -->
    <div v-if="isUploading" class="upload-progress mt-3">
      <div class="progress">
        <div
            class="progress-bar progress-bar-striped progress-bar-animated"
            :style="{ width: uploadProgress + '%' }"
        >
          {{ uploadProgress }}%
        </div>
      </div>
      <small class="text-muted">Uploading files...</small>
    </div>

    <!-- Error Messages -->
    <div v-if="uploadError" class="alert alert-danger mt-3">
      <i class="fas fa-exclamation-triangle mr-2"></i>
      {{ uploadError }}
    </div>

    <!-- File Guidelines -->
    <div class="upload-guidelines mt-3">
      <div class="card bg-light">
        <div class="card-body">
          <h6 class="card-title">
            <i class="fas fa-info-circle text-info mr-2"></i>
            Document Guidelines
          </h6>
          <ul class="small mb-0">
            <li>Upload clear, readable copies of your documents</li>
            <li>Ensure all text and details are visible</li>
            <li>Accepted formats: PDF, JPG, JPEG, PNG</li>
            <li>Maximum file size: {{ maxSize }}MB per file</li>
            <li>You can upload multiple documents at once</li>
          </ul>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'DocumentUpload',
  props: {
    allowedTypes: {
      type: Array,
      default: () => ['pdf', 'jpg', 'jpeg', 'png']
    },
    maxSize: {
      type: Number,
      default: 5 // MB
    }
  },
  data() {
    return {
      uploadedFiles: [],
      isDragOver: false,
      isUploading: false,
      uploadProgress: 0,
      uploadError: null
    }
  },
  computed: {
    acceptedFileTypes() {
      return this.allowedTypes.map(type => `.${type}`).join(',')
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.processFiles(files)
    },

    handleDrop(event) {
      event.preventDefault()
      this.isDragOver = false
      const files = Array.from(event.dataTransfer.files)
      this.processFiles(files)
    },

    handleDragOver(event) {
      event.preventDefault()
      this.isDragOver = true
    },

    handleDragLeave() {
      this.isDragOver = false
    },

    processFiles(files) {
      this.uploadError = null
      const validFiles = []

      for (const file of files) {
        if (this.validateFile(file)) {
          validFiles.push(file)
        }
      }

      if (validFiles.length > 0) {
        this.uploadFiles(validFiles)
      }
    },

    validateFile(file) {
      // Check file type
      const fileType = file.name.split('.').pop().toLowerCase()
      if (!this.allowedTypes.includes(fileType)) {
        this.uploadError = `File type "${fileType}" is not allowed. Allowed types: ${this.allowedTypes.join(', ')}`
        return false
      }

      // Check file size (convert MB to bytes)
      const maxSizeBytes = this.maxSize * 1024 * 1024
      if (file.size > maxSizeBytes) {
        this.uploadError = `File "${file.name}" is too large. Maximum size: ${this.maxSize}MB`
        return false
      }

      // Check if file already exists
      if (this.uploadedFiles.some(f => f.name === file.name && f.size === file.size)) {
        this.uploadError = `File "${file.name}" has already been uploaded`
        return false
      }

      return true
    },

    async uploadFiles(files) {
      this.isUploading = true
      this.uploadProgress = 0

      try {
        // Simulate file upload progress
        for (let i = 0; i < files.length; i++) {
          const file = files[i]

          // Add file to uploaded files list
          this.uploadedFiles.push({
            name: file.name,
            size: file.size,
            type: file.type,
            file: file,
            uploadedAt: new Date().toISOString()
          })

          // Update progress
          this.uploadProgress = Math.round(((i + 1) / files.length) * 100)

          // Simulate upload delay
          await new Promise(resolve => setTimeout(resolve, 500))
        }

        // Emit files to parent component
        this.$emit('files-uploaded', this.uploadedFiles)

      } catch (error) {
        this.uploadError = 'Error uploading files. Please try again.'
        console.error('Upload error:', error)
      } finally {
        this.isUploading = false
        this.uploadProgress = 0
      }
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1)
      this.$emit('files-uploaded', this.uploadedFiles)
    },

    getFileIcon(fileType) {
      const type = fileType.toLowerCase()
      if (type.includes('pdf')) return 'fas fa-file-pdf text-danger'
      if (type.includes('image')) return 'fas fa-file-image text-success'
      if (type.includes('word')) return 'fas fa-file-word text-primary'
      return 'fas fa-file text-secondary'
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.document-upload {
  max-width: 100%;
}

.upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 10px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafbfc;
}

.upload-area:hover {
  border-color: #007bff;
  background: #f8f9fa;
}

.upload-area.drag-over {
  border-color: #007bff;
  background: #e3f2fd;
  transform: scale(1.02);
}

.upload-content {
  pointer-events: none;
}

.upload-icon {
  font-size: 3rem;
  color: #6c757d;
  margin-bottom: 15px;
}

.upload-area:hover .upload-icon {
  color: #007bff;
}

.uploaded-files h6 {
  color: #495057;
  font-weight: 600;
  margin-bottom: 15px;
}

.file-list {
  max-height: 200px;
  overflow-y: auto;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 8px;
  background: white;
  transition: all 0.3s ease;
}

.file-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0,123,255,0.1);
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.file-icon {
  font-size: 1.5rem;
  margin-right: 12px;
}

.file-details {
  display: flex;
  flex-direction: column;
}

.file-name {
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.file-size {
  color: #6c757d;
  font-size: 12px;
}

.file-actions {
  margin-left: 10px;
}

.upload-progress {
  margin-top: 15px;
}

.progress {
  height: 8px;
  border-radius: 4px;
  background: #e9ecef;
}

.progress-bar {
  background: linear-gradient(45deg, #007bff, #0056b3);
}

.upload-guidelines .card {
  border: none;
  border-radius: 8px;
}

.upload-guidelines .card-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 10px;
}

.upload-guidelines ul {
  padding-left: 20px;
}

.upload-guidelines li {
  margin-bottom: 5px;
  color: #6c757d;
}

/* Responsive Design */
@media (max-width: 768px) {
  .upload-area {
    padding: 30px 15px;
  }

  .upload-icon {
    font-size: 2.5rem;
  }

  .file-item {
    padding: 10px;
  }

  .file-details {
    min-width: 0;
  }

  .file-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
}
</style>